import { VideoBackground } from '../components/VideoBackground'
import { useSoundEffects } from '../hooks/useSoundEffects'
import { useState, useEffect } from 'react'
import { AnimatedStarField } from '../components/AnimatedStarField'

export function CreditsPage() {
  const { playHoverSound } = useSoundEffects()
  const [isVisible, setIsVisible] = useState(false)

  // Enhanced credits data with more detailed information
  const credits = {
    core: [
      { role: "Project Lead", name: "SWG: Origins Team", description: "Vision and leadership" },
      { role: "Development Director", name: "Core Development Team", description: "Technical implementation" },
      { role: "Community Manager", name: "Community Team", description: "Player engagement and support" },
      { role: "Art Director", name: "Creative Team", description: "Visual design and assets" },
      { role: "Sound Design", name: "Audio Team", description: "Music and sound effects" },
      { role: "Quality Assurance", name: "Testing Team", description: "Bug testing and quality control" }
    ],
    contributors: [
      { role: "Community Contributors", name: "Amazing Players", description: "Feedback and suggestions" },
      { role: "Beta Testers", name: "Dedicated Testers", description: "Early testing and feedback" },
      { role: "Content Creators", name: "Streamers & YouTubers", description: "Community content creation" },
      { role: "Moderators", name: "Community Moderators", description: "Community management" }
    ],
    special: [
      { role: "Special Thanks", name: "Everyone Who Believed", description: "Support and encouragement" },
      { role: "In Memory Of", name: "The Original SWG Team", description: "Sony Online Entertainment" },
      { role: "Powered By", name: "Core3 Engine", description: "Open source SWG server" },
      { role: "Dedicated To", name: "The Star Wars Galaxies Community", description: "Past, present, and future" }
    ]
  }

  useEffect(() => {
    setIsVisible(true)
  }, [])

  return (
    <div className="video-container min-h-screen flex flex-col relative credits-page scan-lines pt-20">
      {/* Animated Star Field Background */}
      <AnimatedStarField />

      {/* Animated Video Background for Credits */}
      <VideoBackground 
        videoSrc="/media/backgrounds/space-stars-animation.webm"
        fallbackImage="/media/backgrounds/space.jpg"
      />

      {/* Holographic Particles Effect */}
      <div className="holographic-particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="holographic-particle" style={{
            animationDelay: `${i * 0.5}s`,
            left: `${Math.random() * 100}%`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}></div>
        ))}
      </div>

      {/* Credits Crawl */}
      <section className="flex-1 relative z-10 overflow-hidden">
        <div className="credits-crawl">
          <div className={`crawl-content ${isVisible ? 'crawl-visible' : ''}`}>
            <div className="crawl-text">
              <h1 className="crawl-title text-cyan-400 glow-text">CREDITS</h1>
              <p className="crawl-subtitle text-cyan-300">Episode I</p>
              <p className="crawl-subtitle text-cyan-300">A NEW BEGINNING</p>
              
              <div className="crawl-body text-cyan-200">
                {/* Core Team Section */}
                <h2 className="crawl-section-title text-cyan-300 glow-text">CORE TEAM</h2>
                
                {credits.core.map((credit, index) => (
                  <div 
                    key={index} 
                    className="credit-entry enhanced-credit"
                    onMouseEnter={playHoverSound}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="credit-role">{credit.role}</div>
                    <div className="credit-name">{credit.name}</div>
                    <div className="credit-description">{credit.description}</div>
                  </div>
                ))}

                {/* Contributors Section */}
                <h2 className="crawl-section-title text-cyan-300 glow-text">COMMUNITY CONTRIBUTORS</h2>
                
                {credits.contributors.map((credit, index) => (
                  <div 
                    key={index} 
                    className="credit-entry enhanced-credit"
                    onMouseEnter={playHoverSound}
                    style={{ animationDelay: `${(index + credits.core.length) * 0.1}s` }}
                  >
                    <div className="credit-role">{credit.role}</div>
                    <div className="credit-name">{credit.name}</div>
                    <div className="credit-description">{credit.description}</div>
                  </div>
                ))}

                {/* Special Thanks Section */}
                <h2 className="crawl-section-title text-cyan-300 glow-text">SPECIAL THANKS</h2>
                
                {credits.special.map((credit, index) => (
                  <div 
                    key={index} 
                    className="credit-entry enhanced-credit special-credit"
                    onMouseEnter={playHoverSound}
                    style={{ animationDelay: `${(index + credits.core.length + credits.contributors.length) * 0.1}s` }}
                  >
                    <div className="credit-role">{credit.role}</div>
                    <div className="credit-name">{credit.name}</div>
                    <div className="credit-description">{credit.description}</div>
                  </div>
                ))}

                {/* Enhanced Special Thanks Text */}
                <div className="special-thanks-section">
                  <p className="thanks-text">
                    We extend our deepest gratitude to everyone who has supported this project, 
                    whether through development, testing, community building, or simply believing in our vision.
                  </p>

                  <p className="thanks-text">
                    The Force is strong with this community, and together we will continue to preserve 
                    and enhance the legacy of Star Wars Galaxies for generations to come.
                  </p>

                  <p className="crawl-final text-cyan-400 glow-text">
                    May the Force be with you, always.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
